import { alpha, createTheme } from '@mui/material/styles';
import { createElement } from 'react';
import { arEG, enUS as materialEnUS } from '@mui/material/locale';
import { arSD, enUS as dataGridEnUS } from '@mui/x-data-grid/locales';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type { HelixDirection, HelixMode } from './tokens';
import { helixTokens } from './tokens';
import { TablePaginationControls } from '@shared/components/TablePageNavigation';
import { DropdownChevron } from '../components/DropdownChevron';
import dropdownCheck from '../assets/dropdown-check.svg';

export function createHelixTheme(mode: HelixMode, direction: HelixDirection) {
  const colors = helixTokens.color[mode];
  const materialLocale = direction === 'rtl' ? arEG : materialEnUS;
  const dataGridLocale = direction === 'rtl' ? arSD : dataGridEnUS;
  const fontFamily =
    direction === 'rtl' ? helixTokens.typography.arabic : helixTokens.typography.sans;
  const onAccent = mode === 'dark' ? colors.background.subtle : colors.text.inverse;
  const chipTones = {
    default: { main: colors.text.secondary, soft: colors.background.muted },
    primary: { main: colors.primary.text, soft: colors.primary.soft },
    secondary: { main: colors.primary.text, soft: colors.primary.soft },
    success: colors.semantic.success,
    warning: colors.semantic.warning,
    error: colors.semantic.danger,
    info: colors.semantic.info,
  };
  const menuHighlight = { backgroundColor: colors.primary.solid, color: colors.text.inverse };
  const selectionIndicator = {
    content: '""',
    position: 'absolute' as const,
    insetInlineStart: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 10,
    height: 10,
    backgroundColor: 'currentColor',
    mask: `url("${dropdownCheck}") center / contain no-repeat`,
    pointerEvents: 'none' as const,
  };

  return createTheme(
    {
      direction,
      palette: {
        mode,
        DataGrid: {
          bg: colors.background.surface,
          headerBg: colors.background.muted,
          pinnedBg: colors.background.muted,
        },
        primary: {
          main: colors.primary.main,
          dark: colors.primary.strong,
          light: colors.primary.soft,
          contrastText: onAccent,
        },
        success: {
          main: colors.semantic.success.main,
          light: colors.semantic.success.soft,
          contrastText: onAccent,
        },
        warning: {
          main: colors.semantic.warning.main,
          light: colors.semantic.warning.soft,
          contrastText: onAccent,
        },
        error: {
          main: colors.semantic.danger.main,
          light: colors.semantic.danger.soft,
          contrastText: onAccent,
        },
        info: {
          main: colors.semantic.info.main,
          light: colors.semantic.info.soft,
          contrastText: onAccent,
        },
        background: { default: colors.background.canvas, paper: colors.background.surface },
        text: {
          primary: colors.text.primary,
          secondary: colors.text.secondary,
          disabled: colors.text.tertiary,
        },
        divider: colors.border.default,
        action: {
          hover: alpha(colors.text.primary, 0.04),
          selected: colors.primary.soft,
          focus: colors.primary.ring,
          disabled: colors.text.tertiary,
          disabledBackground: colors.background.muted,
        },
      },
      shape: { borderRadius: helixTokens.radius.sm },
      typography: {
        fontFamily,
        fontSize: helixTokens.typography.size.body,
        h1: { fontSize: '1.5rem', lineHeight: 1.25, fontWeight: 700, letterSpacing: '-0.02em' },
        h2: { fontSize: '1.25rem', lineHeight: 1.3, fontWeight: 700, letterSpacing: '-0.005em' },
        h3: { fontSize: '1.125rem', lineHeight: 1.35, fontWeight: 700 },
        h4: { fontSize: '1.5rem', lineHeight: 1.25, fontWeight: 700, letterSpacing: '-0.02em' },
        h5: { fontSize: '1.25rem', lineHeight: 1.3, fontWeight: 700 },
        h6: { fontSize: '1rem', lineHeight: 1.4, fontWeight: 600 },
        button: {
          fontSize: '0.875rem',
          lineHeight: '20px',
          fontWeight: 500,
          textTransform: 'none',
        },
        body1: { fontSize: '0.875rem', lineHeight: '20px' },
        body2: { fontSize: '0.875rem', lineHeight: '20px' },
        subtitle1: { fontSize: '1rem', lineHeight: '24px', fontWeight: 500 },
        subtitle2: { fontSize: '0.875rem', lineHeight: '20px', fontWeight: 600 },
        caption: { fontSize: '0.75rem', lineHeight: '16px' },
        overline: {
          fontSize: '0.625rem',
          lineHeight: '16px',
          fontWeight: 700,
          letterSpacing: '0.08em',
        },
      },
      components: {
        MuiTypography: {
          styleOverrides: {
            root: {
              '&:is(code, kbd, pre), &.mono': { fontFamily: helixTokens.typography.mono },
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            ':root': { colorScheme: mode },
            body: { minWidth: 320 },
            'input, textarea, select, button': { fontFamily: 'inherit' },
            'code, kbd, pre, .mono': { fontFamily: helixTokens.typography.mono },
            '.mono': { fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' },
            '::selection': { backgroundColor: colors.primary.soft, color: colors.primary.text },
            '*:focus-visible': { outline: `2px solid ${colors.primary.main}`, outlineOffset: 2 },
            '@keyframes helixPanelIn': {
              from: { opacity: 0, transform: 'translateY(5px)' },
              to: { opacity: 1, transform: 'none' },
            },
            '@keyframes helixFadeUp': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'none' },
            },
            '@media (prefers-reduced-motion: no-preference)': {
              '.MuiButton-root, .MuiIconButton-root': {
                transition: `transform ${helixTokens.motion.press}ms ${helixTokens.motion.standard}, box-shadow ${helixTokens.motion.press}ms ease, background-color ${helixTokens.motion.press}ms ease`,
              },
              '.MuiButton-root:active:not(.Mui-disabled), .MuiIconButton-root:active:not(.Mui-disabled)':
                { transform: 'scale(0.97)' },
              '[role="tabpanel"]:not([hidden])': {
                animation: `helixPanelIn ${helixTokens.motion.panel}ms ${helixTokens.motion.emphasized}`,
              },
              '.helix-nav-item svg': {
                transition: `transform ${helixTokens.motion.fast}ms ${helixTokens.motion.standard}`,
              },
              '.helix-nav-item:hover svg': { transform: 'scale(1.1)' },
              '.MuiTableBody-root .MuiTableRow-root, .MuiDataGrid-row, .helix-table-row': {
                transition: `background-color ${helixTokens.motion.rowHover}ms ease`,
              },
              '.helix-stagger > *': {
                animation: `helixFadeUp ${helixTokens.motion.reveal}ms ${helixTokens.motion.standard} both`,
              },
              ...Object.fromEntries(
                Array.from({ length: 8 }, (_, index) => [
                  `.helix-stagger > :nth-child(${index + 1})`,
                  { animationDelay: `${20 + index * helixTokens.motion.stagger}ms` },
                ]),
              ),
            },
            '@media (prefers-reduced-motion: reduce)': {
              '*, *::before, *::after': {
                animationDuration: '0.01ms !important',
                animationDelay: '0ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
                scrollBehavior: 'auto !important',
              },
            },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: helixTokens.radius.sm,
              minHeight: helixTokens.control.medium,
              padding: '6px 12px',
              '& .MuiButton-startIcon > *, & .MuiButton-endIcon > *': { fontSize: 16 },
              '&.MuiButton-contained.MuiButton-colorPrimary:not(.Mui-disabled)': {
                backgroundColor: colors.primary.solid,
                color: colors.text.inverse,
                '&:hover': { backgroundColor: colors.primary.solidHover },
              },
            },
            sizeSmall: {
              minHeight: helixTokens.control.small,
              padding: '4px 8px',
              fontSize: 12,
              lineHeight: '16px',
            },
            sizeLarge: {
              minHeight: helixTokens.control.large,
              padding: '8px 16px',
              fontSize: 16,
              lineHeight: '24px',
            },
            outlined: { borderColor: colors.border.strong },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: helixTokens.radius.sm,
              padding: 8,
              '& .MuiSvgIcon-root': { fontSize: 16 },
            },
            sizeSmall: { padding: 4 },
            sizeLarge: { padding: 10, '& .MuiSvgIcon-root': { fontSize: 20 } },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none' },
            rounded: { borderRadius: helixTokens.radius.md },
            outlined: { borderColor: colors.border.default },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              border: `1px solid ${colors.border.default}`,
              borderRadius: helixTokens.radius.md,
              boxShadow: 'none',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: { paper: { borderInlineEnd: `1px solid ${colors.border.default}` } },
        },
        MuiTextField: { defaultProps: { size: 'small' } },
        MuiSelect: {
          defaultProps: {
            size: 'small',
            IconComponent: DropdownChevron,
            MenuProps: {
              marginThreshold: 8,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: direction === 'rtl' ? 'right' : 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: direction === 'rtl' ? 'right' : 'left',
              },
              slotProps: {
                paper: {
                  sx: {
                    mt: 0.5,
                    maxHeight: 'min(320px, calc(100dvh - 32px))',
                    maxWidth: 'calc(100vw - 16px)',
                  },
                },
              },
            },
          },
          styleOverrides: {
            root: { borderRadius: helixTokens.radius.sm, minWidth: 0, fontWeight: 400 },
            select: {
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '&:focus': { backgroundColor: 'transparent' },
            },
            icon: {
              width: 9,
              height: 9,
              right: 12,
              top: 'calc(50% - 4.5px)',
              color: colors.text.secondary,
              opacity: 0.9,
            },
            iconOpen: { transform: 'none' },
          },
        },
        MuiAutocomplete: {
          defaultProps: { size: 'small', popupIcon: createElement(DropdownChevron) },
          styleOverrides: {
            root: { minWidth: 0 },
            inputRoot: {
              '&.MuiOutlinedInput-root': {
                paddingTop: 0,
                paddingBottom: 0,
                paddingInlineStart: 0,
                borderRadius: helixTokens.radius.sm,
                minHeight: 32,
                '& .MuiAutocomplete-input': { padding: '6px 12px', height: 20 },
              },
            },
            paper: {
              marginTop: 4,
              border: `1px solid ${colors.border.default}`,
              borderRadius: 8,
              backgroundColor: colors.background.surface,
              boxShadow: '0 12px 60px rgba(0,0,0,.16), 0 12px 32px -16px rgba(0,0,0,.20)',
            },
            listbox: {
              padding: 8,
              maxHeight: 320,
              scrollbarWidth: 'thin',
              '& .MuiAutocomplete-option': {
                minHeight: 32,
                padding: '6px 24px',
                borderRadius: 4,
                fontSize: 14,
                lineHeight: '20px',
                position: 'relative',
                overflowWrap: 'anywhere',
                color: colors.text.primary,
                '&[aria-selected="true"]': {
                  backgroundColor: 'transparent',
                  '&::before': selectionIndicator,
                },
                '&.Mui-focused, &.Mui-focusVisible, &[aria-selected="true"].Mui-focused, &[aria-selected="true"].Mui-focusVisible':
                  menuHighlight,
                '&[aria-disabled="true"]': {
                  opacity: 0.4,
                  backgroundColor: 'transparent',
                  color: colors.text.secondary,
                },
              },
            },
            popupIndicator: { width: 24, height: 24, color: colors.text.secondary },
            popupIndicatorOpen: { transform: 'none' },
            clearIndicator: { width: 24, height: 24 },
            noOptions: { fontSize: 14, padding: '6px 16px', color: colors.text.secondary },
            loading: { fontSize: 14, padding: '6px 16px', color: colors.text.secondary },
          },
        },
        MuiOutlinedInput: {
          defaultProps: { size: 'small' },
          styleOverrides: {
            root: {
              borderRadius: helixTokens.radius.sm,
              backgroundColor: colors.background.surface,
              fontSize: 14,
              lineHeight: '20px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.strong },
              '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.text.tertiary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 1,
                boxShadow: `0 0 0 2px ${colors.primary.ring}`,
              },
              '&.Mui-disabled': { backgroundColor: colors.background.muted },
              '&.MuiInputBase-sizeSmall .MuiSelect-select': { paddingInlineEnd: 32 },
            },
            input: {
              padding: '10px 12px',
              height: '20px',
              '&::placeholder': { color: colors.text.secondary, opacity: 0.8 },
            },
            sizeSmall: { '& .MuiOutlinedInput-input': { padding: '6px 12px' } },
            multiline: {
              padding: '8px 12px',
              '& textarea.MuiOutlinedInput-input': { padding: 0, height: 'auto' },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: { fontSize: 14 },
            sizeSmall: {
              '&.MuiInputLabel-outlined': {
                transform: 'translate(12px, 6px) scale(1)',
                '&.MuiInputLabel-shrink': { transform: 'translate(12px, -9px) scale(0.75)' },
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: ({ ownerState }) => {
              const tone = chipTones[ownerState.color ?? 'default'];
              return {
                borderRadius: helixTokens.radius.full,
                fontSize: 12,
                fontWeight: 500,
                height: 24,
                color: tone.main,
                backgroundColor: ownerState.variant === 'outlined' ? 'transparent' : tone.soft,
                borderColor: alpha(tone.main, 0.3),
                '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: 'inherit' },
              };
            },
            sizeSmall: { height: 20 },
            label: { paddingInline: 8 },
          },
        },
        MuiMenu: {
          defaultProps: {
            marginThreshold: 8,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: direction === 'rtl' ? 'right' : 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: direction === 'rtl' ? 'right' : 'left',
            },
          },
          styleOverrides: {
            paper: {
              border: `1px solid ${colors.border.default}`,
              borderRadius: helixTokens.radius.md,
              backgroundColor: colors.background.surface,
              boxShadow: '0 12px 60px rgba(0,0,0,.16), 0 12px 32px -16px rgba(0,0,0,.20)',
            },
            list: {
              padding: 8,
              '&:has(.Mui-selected) .MuiMenuItem-root': { paddingInline: 24 },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              minHeight: 32,
              borderRadius: helixTokens.radius.sm,
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '20px',
              color: colors.text.primary,
              padding: '6px 12px',
              '&[role="option"]': {
                paddingInline: 24,
                position: 'relative',
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                '&[aria-selected="true"]::before': selectionIndicator,
              },
              '&.Mui-selected': {
                backgroundColor: 'transparent',
                color: colors.text.primary,
                position: 'relative',
                paddingInline: 24,
                '&::before': selectionIndicator,
              },
              '&:hover:not(.Mui-disabled), &.Mui-focusVisible:not(.Mui-disabled), &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible':
                {
                  ...menuHighlight,
                  '& .MuiListItemIcon-root, & .MuiTypography-root': { color: 'inherit' },
                },
              '&.Mui-disabled': { opacity: 0.4, backgroundColor: 'transparent' },
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              borderRadius: helixTokens.radius.lg,
              border: `1px solid ${colors.border.default}`,
              backgroundImage: 'none',
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: { root: { fontSize: 20, fontWeight: 700, lineHeight: '26px' } },
        },
        MuiTooltip: {
          styleOverrides: { tooltip: { borderRadius: helixTokens.radius.sm, fontSize: 12 } },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontSize: 14,
              fontWeight: 500,
              textTransform: 'none',
              minHeight: 40,
              '&.Mui-selected': { color: colors.primary.text },
            },
          },
        },
        MuiTabs: { styleOverrides: { root: { minHeight: 40 }, indicator: { height: 2 } } },
        MuiTable: {
          defaultProps: { size: 'small' },
          styleOverrides: { root: { backgroundColor: colors.background.surface } },
        },
        MuiTableContainer: {
          styleOverrides: { root: { borderRadius: helixTokens.radius.md, maxWidth: '100%' } },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              fontSize: 14,
              lineHeight: '20px',
              verticalAlign: 'middle',
              borderColor: colors.border.default,
              padding: '12px 16px',
              whiteSpace: 'nowrap',
            },
            head: {
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: colors.background.muted,
              color: colors.text.primary,
              textTransform: 'none',
              letterSpacing: 0,
              padding: '12px 16px',
            },
            paddingCheckbox: { width: 40, padding: '0 8px' },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-hover:hover': { backgroundColor: colors.background.canvas },
              '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: colors.primary.soft },
              '&:focus-visible': { outline: `2px solid ${colors.primary.main}`, outlineOffset: -2 },
              '&:last-child td': { borderBottom: 0 },
            },
          },
        },
        MuiTableSortLabel: {
          styleOverrides: {
            root: { fontWeight: 600 },
            icon: { width: 16, height: 16, opacity: 0.25 },
          },
        },
        MuiTablePagination: {
          defaultProps: { ActionsComponent: TablePaginationControls },
          styleOverrides: {
            root: { color: colors.text.secondary, border: 0, overflow: 'auto', flex: 1 },
            toolbar: { minHeight: 52, padding: '8px 12px', gap: 8, flexWrap: 'wrap' },
            spacer: { display: 'none' },
            selectLabel: { fontSize: 12, margin: 0, order: 1 },
            displayedRows: { fontSize: 12, margin: 0, marginInlineEnd: 'auto', order: 0 },
            select: {
              fontSize: 12,
              border: `1px solid ${colors.border.default}`,
              borderRadius: 6,
              padding: '6px 24px 6px 8px',
              minHeight: 20,
            },
            input: { margin: 0, order: 2 },
            actions: { marginInlineStart: 4, order: 3 },
          },
        },
        MuiDataGrid: {
          defaultProps: { rowHeight: 48, columnHeaderHeight: 44 },
          styleOverrides: {
            root: {
              borderColor: colors.border.default,
              borderRadius: helixTokens.radius.md,
              fontSize: 14,
              backgroundColor: colors.background.surface,
            },
            cell: { padding: '0 16px', alignContent: 'center', borderColor: colors.border.default },
            columnHeader: { padding: '0 16px', fontSize: 13, color: colors.text.primary },
            columnHeaderTitle: { fontWeight: 600 },
            columnSeparator: { color: colors.border.default },
            row: {
              '&:hover': { backgroundColor: colors.background.canvas },
              '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: colors.primary.soft },
            },
            footerContainer: {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
              minHeight: 52,
              '&& .MuiTablePagination-selectLabel, && .MuiTablePagination-input': {
                display: 'inline-flex',
              },
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: helixTokens.radius.md,
              fontSize: 14,
              '&.MuiAlert-standard.MuiAlert-colorSuccess': {
                color: colors.semantic.success.main,
                backgroundColor: colors.semantic.success.soft,
              },
              '&.MuiAlert-standard.MuiAlert-colorWarning': {
                color: colors.semantic.warning.main,
                backgroundColor: colors.semantic.warning.soft,
              },
              '&.MuiAlert-standard.MuiAlert-colorError': {
                color: colors.semantic.danger.main,
                backgroundColor: colors.semantic.danger.soft,
              },
              '&.MuiAlert-standard.MuiAlert-colorInfo': {
                color: colors.semantic.info.main,
                backgroundColor: colors.semantic.info.soft,
              },
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: helixTokens.radius.sm,
              '&.Mui-selected': {
                backgroundColor: colors.primary.soft,
                color: colors.primary.text,
                '&:hover': { backgroundColor: alpha(colors.primary.main, 0.14) },
              },
            },
          },
        },
      },
    },
    materialLocale,
    dataGridLocale,
  );
}
